import React, { useState } from 'react';

import styles from './styles.module.css';
import EyeIcon from '@/assets/icons/eye.svg?react';
import EyeOffIcon from '@/assets/icons/eyeOff.svg?react';
import USFlag from '@/assets/icons/USFlag.svg?react';

interface InputProps {
  label: string;
  value: string;
  onChange?: (value: string) => void;
  type?: 'text' | 'email' | 'password' | 'number';
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  id?: string;
  readOnly?: boolean;
  customClass?: string;
  withFlag?: boolean;
  onlyNumbers?: boolean;
  maxLength?: number;
}

const Input: React.FC<InputProps> = ({
  label,
  value,
  onChange = () => {},
  type = 'text',
  placeholder,
  required = false,
  disabled = false,
  error,
  id,
  readOnly = false,
  customClass,
  withFlag = false,
  onlyNumbers = false,
  maxLength
}) => {
  const inputId = id || `input-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const formGroupClass = `${styles.formGroup} ${customClass}`.trim();
  const isPassword = type === 'password';

  const [showPassword, setShowPassword] = useState(false);
  const inputPasswordType = showPassword ? 'text' : 'password';

  const input = onlyNumbers ? (
    <input
      id={inputId}
      type={type}
      inputMode="numeric"
      pattern="[0-9]*"
      value={value}
      onChange={e => onChange(e.target.value.replace(/\D/g, ''))}
      placeholder={placeholder}
      disabled={disabled}
      maxLength={maxLength}
      className={`${styles.input} ${error ? styles.inputError : ''}`}
      aria-invalid={error ? 'true' : 'false'}
      aria-describedby={error ? `${inputId}-error` : undefined}
    />
  ) : (
    <input
      id={inputId}
      type={isPassword ? inputPasswordType : type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      maxLength={maxLength}
      className={`${styles.input} ${isPassword ? styles.inputPassword : ''} ${error ? styles.inputError : ''}`}
      aria-invalid={error ? 'true' : 'false'}
      aria-describedby={error ? `${inputId}-error` : undefined}
    />
  );

  const inputWithFlag = (
    <div className={styles.inputWithFlag}>
      <span className={styles.flagIcon}>
        <USFlag />
        <span className={styles.flagText}>+1</span>
      </span>
      {input}
    </div>
  );

  return (
    <div className={formGroupClass}>
      <label id={`${label}-input-label`} htmlFor={inputId} className={styles.label}>
        {label}
        {required && <span className={styles.required}>*</span>}
      </label>
      <div className={styles.inputErrorContainer}>
        <div className={styles.inputContainer}>
          {readOnly ? (
            <div id={`${label}-input-read-only`} className={styles.inputReadOnly}>{value}</div>
          ) : withFlag ? (
            inputWithFlag
          ) : (
            input
          )}
          {isPassword && (
            <span
              id={`${label}-eye-icon`}
              className={styles.eyeIcon}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeIcon /> : <EyeOffIcon />}
            </span>
          )}
        </div>
        {error && (
          <span
            id={`${inputId}-error`}
            className={styles.errorMessage}
            role="alert"
          >
            {error}
          </span>
        )}
      </div>
    </div>
  );
};

export default Input;
